import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  DialogActions,
  DialogContent,
  FormHelperText,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMemo, useState } from "react";
import { Controller, FormProvider, useController, useForm, useFormContext } from "react-hook-form";
import { DateTime } from "luxon";
import { v4 as randomUuid } from "uuid";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { DayCellContentArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import deLocale from "@fullcalendar/core/locales/de";
import FullCalendar from "@fullcalendar/react";
import multimonthPlugin from "@fullcalendar/multimonth";
import interactionPlugin, { DateClickArg } from "@fullcalendar/interaction";
import { Column } from "../../misc/Column";
import { Row } from "../../misc/Row";
import { Form2Theme } from "../../misc/Form2";
import { DateTimeInput } from "../../inputs/DateTimeInput";
import { InstructorInput } from "../../inputs/InstructorInput";
import { SelectInput } from "../../inputs/SelectInput";
import { useCurrentDrivingSchool } from "../../hooks/useCurrentDrivingSchool";
import { runSafelyWithServerClient, schemas } from "../../api/server.api";
import { SaveButton2 } from "../../buttons/SaveButton2";
import { type ASFCourseSessionType } from "../../model/autovioCalendarEvents";
import { HoverableNumberCircle } from "../../misc/HoverableNumberCircle";
import { TimePicker } from "@mui/x-date-pickers";
import { useThemeColor } from "../../hooks/useThemeColor";
import { l10n } from "../../l10n";
import { renderAddress } from "../../misc/AddressDisplay";
import { smallSelectInputStyle } from "../../misc/backofficeTheme";
import { TimeInput } from "../../inputs/TimeInput";

const SESSION_TYPES = schemas.ASFCourseSessionTypeEnum.options;

interface Step1FormValues {
  branchId: string;
  instructorId: string;
  sessionStartTime: DateTime;
  plannedDates: Array<DateTime>;
}

interface ASFCourseSessionDraft {
  id: string;
  sessionType: ASFCourseSessionType;
  branchId: string;
  instructorId: string;
  start: DateTime;
  /** only used if sessionType === "fahrprobe" */
  fahrprobeEnd?: DateTime;
}

interface Step2FormValues {
  sessions: Array<ASFCourseSessionDraft>;
}

export function ASFCourseWizard(props: { goBack: () => void; onSuccess: () => void }) {
  const { goBack, onSuccess } = props;
  const [step, setStep] = useState<1 | 2>(1);
  const step1FormProps = useForm<Step1FormValues>({
    defaultValues: {
      branchId: "",
      instructorId: "",
      sessionStartTime: DateTime.fromObject({ hour: 0, minute: 0 }),
      plannedDates: [],
    },
  });
  const [initialSessions, setInitialSessions] = useState<ASFCourseSessionDraft[]>([]);

  function goOn(values: Step1FormValues) {
    const { branchId, instructorId, sessionStartTime, plannedDates } = values;
    const initialSessions = plannedDates.map((plannedDate, index) => {
      const sessionType = SESSION_TYPES[index];
      const start = DateTime.fromObject(
        {
          year: plannedDate.year,
          month: plannedDate.month,
          day: plannedDate.day,
          hour: sessionType === "fahrprobe" ? 8 : sessionStartTime.hour,
          minute: sessionType === "fahrprobe" ? 0 : sessionStartTime.minute,
        },
        { zone: "Europe/Berlin" },
      );
      return {
        id: randomUuid(),
        sessionType,
        branchId,
        instructorId,
        start,
        ...(sessionType === "fahrprobe" ? { fahrprobeEnd: start.plus({ minutes: 600 }) } : {}),
      } satisfies ASFCourseSessionDraft;
    });
    setInitialSessions(initialSessions);
    setStep(2);
  }

  // We need to nest <Step2Form> in the <FormProvider> for <Step1Form> to not lose
  // the entered form values, when the users goes back from step 2 ...
  return (
    <FormProvider {...step1FormProps}>
      {step === 1 && <Step1Form goBack={goBack} goOn={goOn} />}
      {step === 2 && <Step2Form initialSessions={initialSessions} goBack={() => setStep(1)} onSuccess={onSuccess} />}
    </FormProvider>
  );
}

function Step1Form(props: { goBack: () => void; goOn: (formValues: Step1FormValues) => void }) {
  const { goBack, goOn } = props;
  const drivingSchool = useCurrentDrivingSchool();
  const { branches } = drivingSchool;
  const { handleSubmit, control, watch } = useFormContext<Step1FormValues>();

  return (
    <Form2Theme>
      <form onSubmit={handleSubmit(goOn)} style={{ height: "100%" }}>
        <DialogContent>
          <Column gap="1.25em">
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Wähle hier zunächst {branches.length > 1 && "die Filiale,"} den Fahrlehrer, die Startzeit für die
              Gruppensitzungen sowie die Tage, an denen die Gruppensitzungen und die Fahrprobe stattfinden sollen, aus.
              Im nächsten Schritt kannst Du die Eckdaten für die einzelnen Termine noch anpassen.
            </Typography>
            <Row sx={{ gap: 2.5, alignItems: "start" }}>
              {branches.length > 1 && (
                <SelectInput
                  label="Filiale"
                  source="branchId"
                  size="small"
                  options={branches.map((it) => [
                    it.uid,
                    `${it.name} (${renderAddress(it.postalAddress, { oneLine: true })})`,
                  ])}
                  validate={(value) => (value ? true : "Bitte auswählen.")}
                  sx={{ width: "300px" }}
                />
              )}
              <InstructorInput
                source="instructorId"
                branchId={watch("branchId")}
                searchable
                size="small"
                sx={{ width: "300px" }}
              />
              <Controller
                name="sessionStartTime"
                control={control}
                rules={{
                  validate: (value) => {
                    if (!value || (value.get("hour") === 0 && value.get("minute") === 0)) {
                      return "Bitte gib die Startzeit für die Gruppensitzungen ein.";
                    }
                    return true;
                  },
                }}
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                  <TimePicker
                    label="Startzeit"
                    value={value}
                    sx={timePickerStyle}
                    onChange={onChange}
                    disableOpenPicker={false}
                    ampm={false}
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                      },
                    }}
                  />
                )}
              />
            </Row>
            <Select5DaysInput />
          </Column>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={goBack}>
            Zurück
          </Button>
          <Button type="submit" endIcon={<NavigateNextIcon />} variant="contained">
            Weiter
          </Button>
        </DialogActions>
      </form>
    </Form2Theme>
  );
}

function Select5DaysInput() {
  const { control } = useFormContext<Step1FormValues>();
  const {
    field: { value: plannedDatesFieldValue, onChange },
    fieldState: { error },
  } = useController({
    name: "plannedDates",
    control,
    rules: {
      validate: (value) => (value.length === 5 ? true : "Bitte wähle 5 Tage aus."),
    },
  });
  const plannedDates = plannedDatesFieldValue ?? [];
  const errorColor = useThemeColor("error").main;

  function addPlannedDate(cellDetail: DateClickArg) {
    if (
      cellDetail.jsEvent.target instanceof SVGSVGElement ||
      cellDetail.jsEvent.target instanceof SVGPathElement ||
      cellDetail.jsEvent.target instanceof HTMLButtonElement
    ) {
      return;
    }

    if (plannedDates.length === 5) {
      return;
    }

    const cellDate = DateTime.fromJSDate(cellDetail.date);

    if (plannedDates.some((plannedDate) => plannedDate.hasSame(cellDate, "day"))) {
      return;
    }

    const sortedDates = [...plannedDates, cellDate].sort((a, b) => a.toMillis() - b.toMillis());
    onChange(sortedDates);
  }

  function removePlannedDate(date: DateTime) {
    onChange(plannedDates.filter((plannedDate) => !plannedDate.hasSame(date, "day")));
  }

  function renderDayCellContent(options: DayCellContentArg) {
    const date_ = DateTime.fromJSDate(options.date);
    const index = plannedDates.findIndex((plannedDate) => plannedDate.hasSame(date_, "day"));
    return (
      <Row
        sx={{
          p: 0,
          maxWidth: "45px",
          minWidth: "45px",
          height: "47px",
          position: "absolute",
          top: "0%",
          right: 0,
          zIndex: 1,
          gap: "3px",
          justifyContent: "center",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <Row
          sx={{ width: "100%", height: "100%", justifyContent: "center", alignItems: "center", position: "relative" }}
        >
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {options.dayNumberText}
          </Typography>
          {index >= 0 && (
            <Box sx={{ top: "2px", left: "1px" }} position="absolute">
              <HoverableNumberCircle
                onClick={() => removePlannedDate(date_)}
                label={index === 0 ? "S1" : index === 1 ? "F" : `S${index}`}
              />
            </Box>
          )}
        </Row>
      </Row>
    );
  }

  const calendarEventInputs = useMemo(
    () => plannedDates.map((plannedDate) => ({ id: plannedDate.toISODate() })),
    [plannedDates],
  );

  return (
    <Box
      sx={{
        border: `1px solid ${error ? errorColor : "var(--fc-border-color)"}`,
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <FullCalendar
        multiMonthMinWidth={280}
        headerToolbar={headerToolbar}
        dayCellContent={renderDayCellContent}
        eventDisplay="none"
        timeZone="Europe/Berlin"
        locales={[deLocale]}
        height="auto"
        plugins={[dayGridPlugin, multimonthPlugin, interactionPlugin]}
        initialView="sixMonths"
        views={{
          sixMonths: {
            type: "multiMonth",
            duration: { months: 6 },
            multiMonthMaxColumns: 3,
            fixedWeekCount: false,
          },
        }}
        initialDate={DateTime.now().toISO()}
        editable={false}
        dayMaxEvents={1}
        nowIndicator
        events={calendarEventInputs}
        dateClick={addPlannedDate}
      />
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </Box>
  );
}

function Step2Form(props: { goBack: () => void; onSuccess: () => void; initialSessions: ASFCourseSessionDraft[] }) {
  const { goBack, onSuccess, initialSessions } = props;
  const { id: drivingSchoolId, branches, branchesById } = useCurrentDrivingSchool();
  const formProps = useForm<Step2FormValues>({ defaultValues: { sessions: initialSessions } });

  async function onSubmit(values: Step2FormValues) {
    const sessions = values.sessions.map((session) => {
      const { id, sessionType, branchId, instructorId, start, fahrprobeEnd } = session;
      const branch = branchesById[branchId];
      // A Gruppensitzung is always 135 min long ...
      const end = sessionType === "fahrprobe" ? fahrprobeEnd! : start.plus({ minutes: 135 });
      return {
        uid: id,
        start: start.toISO(),
        end: end.toISO(),
        instructorUid: instructorId,
        location: {
          street: branch.postalAddress.street,
          postalCode: branch.postalAddress.postalCode,
          city: branch.postalAddress.city,
        },
      };
    });
    await runSafelyWithServerClient(
      (serverClient) =>
        serverClient.createASFCourse({
          courseUid: randomUuid(),
          drivingSchoolUid: drivingSchoolId,
          sessions,
        }),
      {
        successMessage: "ASF Kurs erfolgreich erstellt.",
        errorMessage: "Fehler beim Erstellen des ASF Kurses.",
        onSuccess,
      },
    );
  }

  return (
    <FormProvider {...formProps}>
      <form onSubmit={formProps.handleSubmit(onSubmit)} style={{ height: "100%" }}>
        <DialogContent>
          <Column gap="1.25em">
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {branches.length > 1
                ? "Prüfe die einzelnen Termine und passe bei Bedarf Datum, Uhrzeit, Fahrlehrer oder Filiale an."
                : "Prüfe die einzelnen Termine und passe bei Bedarf Datum, Uhrzeit oder Fahrlehrer an."}
            </Typography>
            {formProps.watch("sessions").map((session, index) => (
              <SessionAccordion key={session.id} index={index} />
            ))}
          </Column>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between" }}>
          <Button startIcon={<NavigateBeforeIcon />} variant="outlined" onClick={goBack}>
            Zurück
          </Button>
          <SaveButton2 label="Kurs erstellen" />
        </DialogActions>
      </form>
    </FormProvider>
  );
}

function SessionAccordion(props: { index: number }) {
  const { index } = props;
  const { watch } = useFormContext<Step2FormValues>();
  const session = watch(`sessions.${index}`);
  const { sessionType } = session;
  const { branches } = useCurrentDrivingSchool();

  return (
    <Accordion
      square
      sx={{
        boxShadow: "none",
        margin: 0,
        "&.Mui-expanded": { margin: 0 },
        "&::before": { display: "none" },
      }}
      defaultExpanded={sessionType === "fahrprobe" /* ... only expand Fahrprobe by default */}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: "48px",
          "&.Mui-expanded": { minHeight: "48px" },
          "& .MuiAccordionSummary-content": { margin: "12px 0" },
          "& .MuiAccordionSummary-content.Mui-expanded": { margin: "12px 0" },
        }}
      >
        <Typography>
          {`${l10n.asfCourseSessionTitle(sessionType)}: `}
          <Typography component="span" fontWeight="bold">
            {`${session.start.setLocale("de-DE").toFormat("ccc, dd.MM.yyyy HH:mm")} Uhr`}
          </Typography>
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Row sx={{ marginTop: 1 }} gap={3} alignItems="center">
          <DateTimeInput source={`sessions.${index}.start`} size="small" allowPastDates sx={{ width: "200px" }} />
          <InstructorInput
            source={`sessions.${index}.instructorId`}
            branchId={session.branchId}
            searchable
            sx={smallSelectInputStyle}
          />
          {branches.length > 1 && (
            <SelectInput
              source={`sessions.${index}.branchId`}
              label="Filiale"
              options={branches.map((it) => [
                it.uid,
                `${it.name} (${renderAddress(it.postalAddress, { oneLine: true })})`,
              ])}
              sx={{ ...smallSelectInputStyle, maxWidth: "300px" }}
            />
          )}
        </Row>
        {sessionType === "fahrprobe" && (
          <Row sx={{ marginTop: 3 }} gap={3}>
            <TimeInput label="bis" source={`sessions.${index}.fahrprobeEnd`} size="small" sx={{ width: "200px" }} />
            <Typography component="div" variant="body2" sx={{ color: "text.secondary" }}>
              Dieser Termin dient nur als Blocker im Kalender des Fahrlehrers. Für die tatsächlichen Fahrproben mit den
              einzelnen Kursteilnehmern können später Fahrstunden angelegt werden.
            </Typography>
          </Row>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

const timePickerStyle = {
  "& .MuiInputBase-root": { maxWidth: "130px", minWidth: "130px" },
  "& .MuiInputBase-input": { py: "8.5px" },
};

const headerToolbar = { left: "title", center: "", right: "prev today next" };
